import axios from 'axios';
import { scopedChannelApi } from '@/session/scopedClients';
import { requestScopeFor, type RequestScope } from '@/session/scopedTransport';
import {
  acquireOperationContext,
  operationRequirement,
  SessionTransitionError,
  type OperationContext,
  type OperationPhase,
} from '@/session/operation';
import type { OrganizationId } from '@/session/identity';
import { extractContentDispositionFilename } from '@/utils/contentDisposition';

export type SuperTemplateStatus = {
  status: 'success' | 'failed' | 'pending' | (string & {});
  file?: string;
  reason?: string;
  updateAt?: string;
};

export type SuperTemplateRequestOptions = {
  errorsOnly?: boolean;
  listingActions?: string[];
  inStockOnly?: boolean;
  requireFieldsOnly?: boolean;
  macroEnabled?: boolean;
  skipMissingFields?: boolean;
};

export type SuperTemplateWorkflow = {
  operation: OperationContext;
  scope: RequestScope;
  signal: AbortSignal;
  cancel(): void;
  isActive(): boolean;
  isScopeCurrent(): boolean;
};

const POLL_INTERVAL_MS = 3000;

const parseSuperTemplateStatus = (value: unknown): SuperTemplateStatus | null => {
  if (!value || typeof value !== 'object') return null;
  const status = (value as { status?: unknown }).status;
  return typeof status === 'string' ? (value as SuperTemplateStatus) : null;
};

export class SuperTemplateTimeoutError extends Error {
  constructor() {
    super('Timed out waiting for super template generation');
    this.name = 'SuperTemplateTimeoutError';
  }
}

export const createSuperTemplateWorkflow = (organization: OrganizationId): SuperTemplateWorkflow => {
  const operation = acquireOperationContext(organization);
  const lifecycle = new AbortController();
  const signal = AbortSignal.any([lifecycle.signal, operation.actorSignal]);

  return {
    operation,
    scope: requestScopeFor(organization),
    signal,
    cancel: () => lifecycle.abort(),
    isActive: () => !signal.aborted && operation.isActorCurrent(),
    isScopeCurrent: () => !lifecycle.signal.aborted && operation.isScopeCurrent(),
  };
};

const requireActive = (workflow: SuperTemplateWorkflow): void => {
  if (!workflow.isActive()) throw new SessionTransitionError();
};

const requireScopeCurrent = (workflow: SuperTemplateWorkflow): void => {
  if (!workflow.isScopeCurrent()) throw new SessionTransitionError();
};

const waitForNextPoll = (signal: AbortSignal, intervalMs: number): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new SessionTransitionError());
      return;
    }

    const handleAbort = () => {
      clearTimeout(timer);
      reject(new SessionTransitionError());
    };
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', handleAbort);
      resolve();
    }, intervalMs);
    signal.addEventListener('abort', handleAbort, { once: true });
  });

export const requestSuperTemplateGeneration = async ({
  workflow,
  subscriptionId,
  postingTemplateId,
  options = {},
  phase = 'start',
}: {
  workflow: SuperTemplateWorkflow;
  subscriptionId: string | number;
  postingTemplateId: string | number;
  options?: SuperTemplateRequestOptions;
  phase?: Extract<OperationPhase, 'start' | 'continue'>;
}): Promise<string | number> => {
  if (phase === 'start') requireScopeCurrent(workflow);
  else requireActive(workflow);

  const response = await scopedChannelApi(workflow.scope).post<{ id?: string | number }>(
    'astur/integrations/channel/uber_template/request',
    {
      report_type: 'uber_template',
      posting_template_id: postingTemplateId,
      errors_only: options.errorsOnly ?? false,
      listing_actions: options.listingActions ?? ['FULL', 'ZERO'],
      in_stock_only: options.inStockOnly ?? false,
      require_fields_only: options.requireFieldsOnly ?? false,
      macro_enabled: options.macroEnabled ?? false,
      skip_missing_fields: options.skipMissingFields ?? false,
    },
    {
      headers: { 'X-astur-subscription-id': String(subscriptionId) },
      operationRequirement: operationRequirement(workflow.operation, phase),
    }
  );

  requireActive(workflow);
  if (response.data.id === undefined || response.data.id === null) throw new Error('Super template generation did not return a reference');
  return response.data.id;
};

export const getSuperTemplateStatus = async ({
  workflow,
  subscriptionId,
  referenceId,
}: {
  workflow: SuperTemplateWorkflow;
  subscriptionId: string | number;
  referenceId: string | number;
}): Promise<SuperTemplateStatus> => {
  requireActive(workflow);
  try {
    const response = await scopedChannelApi(workflow.scope).get<SuperTemplateStatus>(
      'astur/integrations/channel/uber_template/status/' + referenceId,
      {
        headers: { Accept: 'application/json', 'x-astur-subscription-id': String(subscriptionId) },
        signal: workflow.signal,
        operationRequirement: operationRequirement(workflow.operation, 'continue'),
      }
    );
    requireActive(workflow);
    return response.data;
  } catch (error) {
    requireActive(workflow);
    const responseStatus = axios.isAxiosError(error) ? parseSuperTemplateStatus(error.response?.data) : null;
    if (responseStatus) return responseStatus;
    throw error;
  }
};

export const pollSuperTemplateStatus = async ({
  workflow,
  subscriptionId,
  referenceId,
  maxAttempts = Number.POSITIVE_INFINITY,
  retryErrors = false,
  intervalMs = POLL_INTERVAL_MS,
}: {
  workflow: SuperTemplateWorkflow;
  subscriptionId: string | number;
  referenceId: string | number;
  maxAttempts?: number;
  retryErrors?: boolean;
  intervalMs?: number;
}): Promise<SuperTemplateStatus> => {
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    requireActive(workflow);
    await waitForNextPoll(workflow.signal, intervalMs);
    try {
      const result = await getSuperTemplateStatus({ workflow, subscriptionId, referenceId });
      if (result.status === 'success' || result.status === 'failed') return result;
    } catch (error) {
      requireActive(workflow);
      if (!retryErrors) throw error;
    }
  }

  requireActive(workflow);
  throw new SuperTemplateTimeoutError();
};

export const fetchSuperTemplateFile = async ({
  workflow,
  fileUrl,
  defaultFilename = 'super_template.xlsx',
}: {
  workflow: SuperTemplateWorkflow;
  fileUrl: string;
  defaultFilename?: string;
}): Promise<{ blob: Blob; filename: string }> => {
  requireScopeCurrent(workflow);
  try {
    const signal = AbortSignal.any([workflow.signal, workflow.operation.scopeSignal]);
    const response = await fetch(fileUrl, { credentials: 'omit', signal });
    if (!response.ok) throw new Error('Error downloading super template');
    const filename = extractContentDispositionFilename(response.headers.get('Content-Disposition'), defaultFilename);
    const blob = await response.blob();
    requireScopeCurrent(workflow);
    return { blob, filename };
  } catch (error) {
    if (!workflow.isScopeCurrent()) throw new SessionTransitionError();
    throw error;
  }
};

export const triggerSuperTemplateDownload = (workflow: SuperTemplateWorkflow, blob: Blob, filename: string): void => {
  requireScopeCurrent(workflow);
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.style.display = 'none';
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => window.URL.revokeObjectURL(url), 100);
};
